import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { createSlug } from '../utils/createSlug';
import LoadingComponent from './LoadingComponent';
import Seo from '../Seo';

const SITE_BASE = 'https://indianfooddelight.com';
const fallbackImageUrl = 'https://images.unsplash.com/photo-1540420773420-3366772f4999';

function getIsoDuration(value) {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const isoDuration = text.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i);
  if (isoDuration && isoDuration.slice(1).some(Boolean)) return text.toUpperCase();
  const match = text.match(/^(\d+)\s*(minutes?|mins?|hours?|hrs?)$/i);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isSafeInteger(amount) || amount <= 0) return null;
  const unit = match[2].toLowerCase();
  return unit.startsWith('h') ? `PT${amount}H` : `PT${amount}M`;
}

function getRecipeDescription(recipe) {
  const description = recipe.introduction || recipe.preparation;
  if (typeof description === 'string' && description.trim()) {
    const text = description.trim().replace(/\s+/g, ' ');
    if (text.length <= 160) return text;
    const shortened = text.slice(0, 157);
    const lastSpace = shortened.lastIndexOf(' ');
    return `${shortened.slice(0, lastSpace > 0 ? lastSpace : 157)}...`;
  }
  return '';
}

function getAbsoluteImageUrl(image) {
  if (typeof image !== 'string' || !image.trim()) return null;
  try {
    const url = new URL(image, SITE_BASE);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function serializeJsonLd(value) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function Recipe({ recipe }) {
  const { slug } = useParams();

  const [loadedRecipe, setLoadedRecipe] = useState(null);
  const [routeLoading, setRouteLoading] = useState(() => !recipe && Boolean(slug));
  const [draft, setDraft] = useState(recipe || {});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
  if (recipe || !slug) {
    return;
  }

  const loadRecipeFromSlug = async () => {
    setRouteLoading(true);

    try {
      const response = await api.get(`/recipe/slug/${encodeURIComponent(slug)}`);
      setLoadedRecipe(response.data);
    } catch (error) {
      console.error(
        'Failed to load recipe from URL:',
        error.response?.data || error.message
      );

      setLoadedRecipe(null);
    } finally {
      setRouteLoading(false);
    }
  };

  loadRecipeFromSlug();
}, [recipe, slug]);

  useEffect(() => {
  setDraft(recipe || loadedRecipe || {});
  setIsEditing(false);
}, [recipe, loadedRecipe]);

  if (!recipe && !loadedRecipe && routeLoading) {
    return <LoadingComponent />;
  }

  if (!recipe && !loadedRecipe) {
    return (
      <div className="content-panel" style={{ padding: '24px' }}>
        <Seo
          title="Recipe Not Found | Indian Food Delight"
          description="The requested recipe could not be found."
          path={`/recipes/${slug || ''}`}
          robots="noindex,follow"
        />
        <h1>Recipe Not Found</h1>
        <p>We couldn't find the recipe you're looking for. It may have been removed or the URL is incorrect.</p>
        <p>
          <Link to="/recipes">Return to recipes</Link>
        </p>
      </div>
    );
  }

  const parseListValue = (value = '') =>
    value
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);

  const formatListValue = (values = []) => values.join('\n');

  const getVideoEmbedUrl = (url) => {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname.toLowerCase();
      const pathname = parsedUrl.pathname || '';

      if (hostname.includes('youtube.com')) {
        if (pathname.startsWith('/embed/')) {
          return parsedUrl.toString();
        }
        if (pathname.startsWith('/shorts/')) {
          const videoId = pathname.split('/')[2];
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }
        const videoId = parsedUrl.searchParams.get('v');
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      if (hostname.includes('youtu.be')) {
        const videoId = pathname.slice(1);
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }
    } catch (error) {
      return '';
    }

    return '';
  };

  const videoEmbedUrl = getVideoEmbedUrl(draft.referenceURL);
  const showVideoSection = isEditing || Boolean(videoEmbedUrl);

  // SEO helpers
  const recipeSlug = draft.slug || createSlug(draft.dishName || slug || '');
  const canonicalUrl = `${SITE_BASE}/recipes/${recipeSlug}`;
  const pageTitle = draft.dishName
    ? `${draft.dishName} Recipe | Indian Food Delight`
    : 'Recipe | Indian Food Delight';
  const metaDescription = getRecipeDescription(draft);
  const imageUrl = draft.imageURL
    || (Array.isArray(draft.image) ? draft.image[0] : draft.image)
    || draft.imageUrl
    || draft.photo
    || fallbackImageUrl;
  const socialImageUrl = getAbsoluteImageUrl(
    draft.imageURL || (Array.isArray(draft.image) ? draft.image[0] : draft.image) || draft.imageUrl || draft.photo
  );

  const buildJsonLd = () => {
    if (!draft || !draft.dishName || draft.isValidDish !== true) return null;

    const ld = {
      '@type': 'Recipe',
      name: draft.dishName,
    };

    if (metaDescription) ld.description = metaDescription;
    if (socialImageUrl) ld.image = [socialImageUrl];
    if (draft.dateModified && !Number.isNaN(Date.parse(draft.dateModified))) {
      ld.dateModified = new Date(draft.dateModified).toISOString();
    }

    const prepTime = getIsoDuration(draft.prepTime || draft.preparationTime);
    const cookTime = getIsoDuration(draft.cookingTime);
    const totalTime = getIsoDuration(draft.totalTime);
    if (prepTime) ld.prepTime = prepTime;
    if (cookTime) ld.cookTime = cookTime;
    if (totalTime) ld.totalTime = totalTime;
    if (draft.servingFor) ld.recipeYield = draft.servingFor;
    if (draft.recipeCategory) ld.recipeCategory = draft.recipeCategory;
    if (draft.recipeCuisine) ld.recipeCuisine = draft.recipeCuisine;
    if (draft.keywords) ld.keywords = draft.keywords;

    if (Array.isArray(draft.ingredientsMeasurement) && draft.ingredientsMeasurement.length > 0) {
      ld.recipeIngredient = draft.ingredientsMeasurement
        .map((item) => (item.ingredient
          ? `${item.quantity ? `${item.quantity} ` : ''}${item.ingredient}`.trim()
          : ''))
        .filter(Boolean);
    }

    if (Array.isArray(draft.cookingInstructions) && draft.cookingInstructions.length > 0) {
      ld.recipeInstructions = draft.cookingInstructions
        .filter((step) => typeof step === 'string' && step.trim())
        .map((step) => ({ '@type': 'HowToStep', text: step.trim() }));
    }

    const macros = draft.macronutrients;
    if (macros && typeof macros === 'object') {
      const nutrition = { '@type': 'NutritionInformation' };
      const fields = {
        calories: 'calories',
        carbohydrateContent: 'carbohydrates',
        proteinContent: 'protein',
        fatContent: 'fat',
        fiberContent: 'fiber',
      };
      Object.entries(fields).forEach(([schemaField, recipeField]) => {
        const value = macros[recipeField];
        if (typeof value === 'string' && value.trim()) {
          nutrition[schemaField] = value.trim();
        }
      });
      if (Object.keys(nutrition).length > 1) ld.nutrition = nutrition;
    }

    return ld;
  };

  const recipeJsonLd = buildJsonLd();
  const jsonLd = recipeJsonLd
    ? {
      '@context': 'https://schema.org',
      '@graph': [
        { ...recipeJsonLd, '@context': undefined },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_BASE },
            { '@type': 'ListItem', position: 2, name: 'Recipes', item: `${SITE_BASE}/recipes` },
            { '@type': 'ListItem', position: 3, name: draft.dishName || slug, item: canonicalUrl },
          ],
        },
      ],
    }
    : null;

  const handleFieldChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleIngredientRowChange = (index, field, value) => {
    setDraft((prev) => {
      const nextItems = [...(prev.ingredientsMeasurement || [])];
      nextItems[index] = { ...nextItems[index], [field]: value };
      return { ...prev, ingredientsMeasurement: nextItems };
    });
  };

  const addIngredientRow = () => {
    setDraft((prev) => ({
      ...prev,
      ingredientsMeasurement: [
        ...(prev.ingredientsMeasurement || []),
        { ingredient: '', quantity: '' },
      ],
    }));
  };

  const removeIngredientRow = (index) => {
    setDraft((prev) => ({
      ...prev,
      ingredientsMeasurement: (prev.ingredientsMeasurement || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleMacroValueChange = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      macronutrients: {
        ...(prev.macronutrients || {}),
        [key]: value,
      },
    }));
  };

  const handleCookingInstructionChange = (index, value) => {
    setDraft((prev) => {
      const next = [...(prev.cookingInstructions || [])];
      next[index] = value;
      return { ...prev, cookingInstructions: next };
    });
  };

  const addCookingInstruction = () => {
    setDraft((prev) => ({
      ...prev,
      cookingInstructions: [...(prev.cookingInstructions || []), ''],
    }));
  };

  const removeCookingInstruction = (index) => {
    setDraft((prev) => ({
      ...prev,
      cookingInstructions: (prev.cookingInstructions || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put('/recipe', draft);
    } catch (error) {
      console.error('Recipe update is ready for your backend endpoint.', error);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  return (
    <div className="recipe-page animate-fade-in-up">
      {/* Dynamic SEO metadata for each recipe */}
      <Seo
        title={pageTitle}
        description={metaDescription}
        path={`/recipes/${recipeSlug}`}
        image={socialImageUrl}
        type="article"
      />

      {/* Breadcrumb visible navigation */}
      <nav aria-label="Breadcrumb" style={{ marginBottom: '12px' }}>
        <ol style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '8px', alignItems: 'center', margin: 0 }}>
          <li><Link to="/">Home</Link></li>
          <li>→</li>
          <li><Link to="/recipes">Recipes</Link></li>
          <li>→</li>
          <li aria-current="page">{draft.dishName}</li>
        </ol>
      </nav>

      {jsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
        />
      ) : null}

      <div className="recipe-hero">
        <div className="recipe-hero-top">
          <div className="recipe-hero-copy">
            <h1 className="recipe-title">
              {isEditing ? (
                <input
                  value={draft.dishName || ''}
                  onChange={(event) => handleFieldChange('dishName', event.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
              ) : (
                draft.dishName
              )}
            </h1>
            <p className="recipe-intro">
              {isEditing ? (
                <textarea
                  value={draft.introduction || ''}
                  onChange={(event) => handleFieldChange('introduction', event.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
              ) : (
                draft.introduction
              )}
            </p>
          </div>
          <div className="recipe-hero-side">
            <img
              src={imageUrl}
              alt={draft.dishName || 'Recipe image'}
              className="recipe-hero-image"
              onError={(event) => {
                const image = event.currentTarget;
                if (image.src !== fallbackImageUrl) {
                  image.dataset.fallback = 'true';
                  image.src = fallbackImageUrl;
                }
              }}
              width="900"
              height="650"
              loading="eager"
            />
            <button
              type="button"
              onClick={() => setIsEditing((prev) => !prev)}
              style={{ display: 'none', padding: '8px 14px', borderRadius: '999px', border: '1px solid var(--color-border)', background: 'var(--color-bg)', cursor: 'pointer' }}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            <span className="recipe-badge">
              🍛 {draft.recipeCuisine || 'Recipe'}
            </span>
          </div>
        </div>

        <div className="recipe-meta-grid" style={{ marginTop: '24px' }}>
          <div className="recipe-meta-item">
            <div className="recipe-meta-label">Best Time to Eat</div>
            <div className="recipe-meta-value">
              {isEditing ? (
                <input
                  value={draft.bestTimeToEat || ''}
                  onChange={(event) => handleFieldChange('bestTimeToEat', event.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
              ) : (
                draft.bestTimeToEat
              )}
            </div>
          </div>
          <div className="recipe-meta-item">
            <div className="recipe-meta-label">Serving For</div>
            <div className="recipe-meta-value">
              {isEditing ? (
                <input
                  value={draft.servingFor || ''}
                  onChange={(event) => handleFieldChange('servingFor', event.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
              ) : (
                draft.servingFor
              )}
            </div>
          </div>
          <div className="recipe-meta-item">
            <div className="recipe-meta-label">Cooking Time</div>
            <div className="recipe-meta-value">
              {isEditing ? (
                <input
                  value={draft.cookingTime || ''}
                  onChange={(event) => handleFieldChange('cookingTime', event.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
              ) : (
                draft.cookingTime
              )}
            </div>
          </div>
        </div>
      </div>

        

        {showVideoSection ? (
        <div className="recipe-section animate-fade-in-up delay-100">
          <h2 className="recipe-section-title">🎬 Recipe Video</h2>
          {isEditing ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              <input
                type="url"
                value={draft.referenceURL || ''}
                onChange={(event) => handleFieldChange('referenceURL', event.target.value)}
                placeholder="Paste a YouTube URL here"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
              />
              {videoEmbedUrl ? (
                <div className="recipe-video-frame">
                  <iframe
                    src={videoEmbedUrl}
                    title="Recipe video preview"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="recipe-video-embed"
                  />
                </div>
              ) : (
                <div style={{ padding: '14px', borderRadius: '12px', background: '#111', color: '#ddd' }}>
                  Enter a valid YouTube link to preview the recipe video.
                </div>
              )}
            </div>
          ) : (
            <div className="recipe-video-frame">
              <iframe
                src={videoEmbedUrl}
                title="Recipe video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="recipe-video-embed"
              />
            </div>
          )}
        </div>
      ) : null}

      <div className="recipe-section animate-fade-in-up delay-100">
        <h2 className="recipe-section-title">🌿 Key Ingredients</h2>
        {isEditing ? (
          <textarea
            value={formatListValue(draft.keyIngredients || [])}
            onChange={(event) => handleFieldChange('keyIngredients', parseListValue(event.target.value))}
            rows={6}
            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
          />
        ) : (
          <div className="ingredient-grid">
            {(draft.keyIngredients || []).map((item, i) => (
              <div key={i} className="ingredient-item">{item}</div>
            ))}
          </div>
        )}
      </div>

      {(draft.equipment || []).length > 0 || isEditing ? (
        <div className="recipe-section animate-fade-in-up delay-200">
          <h2 className="recipe-section-title">🍳 Equipment Needed</h2>
          {isEditing ? (
            <textarea
              value={formatListValue(draft.equipment || [])}
              onChange={(event) => handleFieldChange('equipment', parseListValue(event.target.value))}
              rows={4}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
            />
          ) : (
            <div className="ingredient-grid">
              {(draft.equipment || []).map((item, i) => (
                <div key={i} className="ingredient-item">{item}</div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <div className="recipe-section animate-fade-in-up delay-200">
        <h2 className="recipe-section-title">⚖️ Ingredient Proportions</h2>
        {isEditing ? (
          <div style={{ display: 'grid', gap: '10px' }}>
            {(draft.ingredientsMeasurement || []).map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                <input
                  value={item.ingredient || ''}
                  onChange={(event) => handleIngredientRowChange(i, 'ingredient', event.target.value)}
                  placeholder="Ingredient"
                  style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
                <input
                  value={item.quantity || ''}
                  onChange={(event) => handleIngredientRowChange(i, 'quantity', event.target.value)}
                  placeholder="Quantity"
                  style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
                <button type="button" onClick={() => removeIngredientRow(i)} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)', cursor: 'pointer' }}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addIngredientRow} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-border)', cursor: 'pointer', width: 'fit-content' }}>
              + Add ingredient row
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
            {(draft.ingredientsMeasurement || []).map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)',
                fontSize: '0.88rem',
              }}>
                <span style={{ fontWeight: 500, color: 'var(--color-text)' }}>{item.ingredient}</span>
                <span className="badge-custom badge-gold">{item.quantity}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {draft.preparation || isEditing ? (
        <div className="recipe-section animate-fade-in-up delay-300">
          <h2 className="recipe-section-title">📋 Preparation</h2>
          {isEditing ? (
            <textarea
              value={draft.preparation || ''}
              onChange={(event) => handleFieldChange('preparation', event.target.value)}
              rows={5}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
            />
          ) : (
            <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, margin: 0, fontSize: '0.93rem' }}>
              {draft.preparation}
            </p>
          )}
        </div>
      ) : null}

      {!isEditing && (
        <div style={{ marginTop: '20px', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
          <Link
            to="/marketplace"
            aria-label="View recommended marketplace products"
            style={{
              width: '100%',
              maxWidth: '728px',
              height: '90px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary))',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fbfaf8',
              textDecoration: 'none',
              boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
              fontWeight: 700,
              fontSize: '1rem',
              letterSpacing: '0.02em',
            }}
          >
            <span>Buy recommended product on Amazon</span>
          </Link>
        </div>
      )}

      <div className="recipe-section animate-fade-in-up delay-300">
        <h2 className="recipe-section-title">👨‍🍳 Cooking Instructions</h2>
        {isEditing ? (
          <div style={{ display: 'grid', gap: '10px' }}>
            {(draft.cookingInstructions || []).length === 0 ? (
              <div style={{ display: 'grid', gap: '8px' }}>
                <textarea
                  value={''}
                  onChange={(event) => handleCookingInstructionChange(0, event.target.value)}
                  rows={3}
                  placeholder="Describe this step"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <button type="button" onClick={addCookingInstruction} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-border)', cursor: 'pointer' }}>
                    + Add instruction
                  </button>
                </div>
              </div>
            ) : (
              <>
                {(draft.cookingInstructions || []).map((step, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'start' }}>
                    <textarea
                      value={step}
                      onChange={(event) => handleCookingInstructionChange(i, event.target.value)}
                      rows={3}
                      placeholder={`Step ${i + 1}`}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <button type="button" onClick={() => removeCookingInstruction(i)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-border)', cursor: 'pointer' }}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <button type="button" onClick={addCookingInstruction} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-border)', cursor: 'pointer' }}>
                    + Add instruction
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div>
            {(draft.cookingInstructions || []).map((step, i) => (
              <div key={i} className="step-item">
                <div className="step-number">{i + 1}</div>
                <div className="step-text">{step}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        {draft.finishingTouch || isEditing ? (
          <div className="recipe-section animate-fade-in-up delay-400" style={{ margin: 0 }}>
            <h2 className="recipe-section-title">✨ Finishing Touch</h2>
            {isEditing ? (
              <textarea
                value={draft.finishingTouch || ''}
                onChange={(event) => handleFieldChange('finishingTouch', event.target.value)}
                rows={4}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
              />
            ) : (
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, margin: 0, fontSize: '0.93rem' }}>
                {draft.finishingTouch}
              </p>
            )}
          </div>
        ) : null}
        {draft.serving || isEditing ? (
          <div className="recipe-section animate-fade-in-up delay-400" style={{ margin: 0 }}>
            <h2 className="recipe-section-title">🍽️ Serving</h2>
            {isEditing ? (
              <textarea
                value={draft.serving || ''}
                onChange={(event) => handleFieldChange('serving', event.target.value)}
                rows={4}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
              />
            ) : (
              <p style={{ color: 'var(--color-text-muted)', lineHeight: 1.75, margin: 0, fontSize: '0.93rem' }}>
                {draft.serving}
              </p>
            )}
          </div>
        ) : null}
      </div>

      {draft.macronutrients && Object.keys(draft.macronutrients).length > 0 ? (
        <div className="recipe-section animate-fade-in-up delay-500">
          <h2 className="recipe-section-title">📊 Macronutrients</h2>
          <div className="macro-grid" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.entries(draft.macronutrients).map(([key, value]) => (
              <div key={key} className="macro-item" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: '10px' }}>
                <div className="macro-label" style={{ fontWeight: 'bold' }}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </div>
                {isEditing ? (
                  <input
                    value={value || ''}
                    onChange={(event) => handleMacroValueChange(key, event.target.value)}
                    style={{ width: '180px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                  />
                ) : (
                  <div className="macro-value">{value}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {isEditing ? (
        <div
          style={{
            position: 'sticky',
            bottom: '16px',
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 10,
          }}
        >
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid #d97706',
              background: '#f59e0b',
              color: '#111827',
              cursor: 'pointer',
              fontWeight: 700,
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.16)',
            }}
          >
            {isSaving ? 'Updating...' : 'Update'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default Recipe;
